import * as React from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { Icon } from '../icon';
import { Spinner } from '../spinner';
import { CypressI } from 'interfaces/cypress';

export interface ButtonProps {
	type?: 'submit' | 'reset' | 'button';
	className?: string;
	classNameButton?: string;
	disabled?: boolean;
	decoration?:
		| 'primary'
		| 'secondary'
		| 'not-fill'
		| 'line-primary'
		| 'ghost'
		| 'danger'
		| 'danger-outline'
		| 'none';
	size?: 'extra-small' | 'small' | 'medium' | 'large' | 'full' | 'fit';
	onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
	label?: string;
	icon?: string;
	href?: string;
	loading?: boolean;
	iconLeft?: boolean;
	iconRight?: boolean;
	isTargetBlack?: boolean;
	classNameLink?: string;
	classNameLabel?: string;
}

/**
 * Primary UI component for user interaction
 */
const ButtonContent = React.forwardRef<
	HTMLButtonElement,
	ButtonProps & CypressI & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function ButtonContent(
	{
		dataCY,
		type = 'button',
		className,
		disabled = false,
		decoration,
		size,
		onClick,
		children,
		label,
		icon,
		iconLeft,
		iconRight,
		loading,
		classNameButton,
		classNameLabel,
		...rest
	},
	ref
): JSX.Element {
	return (
		<button
			ref={ref}
			data-cy={dataCY}
			type={type}
			disabled={disabled}
			onClick={onClick}
			{...rest}
			className={clsx(
				//size
				{ 'w-full md:w-fit': size === 'fit' },
				{ 'w-full': size === 'full' },
				{ 'w-full md:w-auto h-12 lg:h-16': size === 'large' },
				{ 'w-full md:w-auto h-12': size === 'medium' },
				{ 'w-full md:w-auto h-[30px]': size === 'small' },
				{ 'w-full md:w-36': size === 'extra-small' },

				// primary
				{
					'text-white bg-primary border-primary': decoration === 'primary',
				},
				{
					'hover:bg-[#7637C9] active:bg-[#5614AF] focus:bg-[#5614AF] disabled:bg-[#B18EE0] disabled:border-[#B18EE0]':
						decoration === 'primary',
				},

				// not fill primary
				{
					'text-primary bg-transparent border-primary':
						decoration === 'line-primary',
				},
				{
					'hover:border-[#8964BA] hover:text-[#8964BA]':
						decoration === 'line-primary',
				},
				{
					'focus:focus:border-[#5614AF] focus:text-[#5614AF]':
						decoration === 'line-primary',
				},
				{
					'disabled:focus:border-[#B18EE0] disabled:text-[#B18EE0] disabled:bg-white disabled:border-[#B18EE0]':
						decoration === 'line-primary',
				},

				// secondary
				{
					'text-black border active:bg-[#BEC86B] focus-visible:bg-[#BEC86B] border-green-base':
						decoration === 'secondary',
				},
				{
					'hover:bg-[#F6F8E3] disabled:bg-white disabled:!text-[#97949A]':
						decoration === 'secondary',
				},
				// danger
				{
					'text-white bg-main-red border-main-red': decoration === 'danger',
				},
				{
					'disabled:border-opacity-20 disabled:bg-[#FF668E]':
						decoration === 'danger',
				},
				//danger outline
				{
					'text-main-red bg-white border-main-red hover:bg-[#FF00431A] active:bg-[#FF00433D] focus:bg-[#FF00433D]':
						decoration === 'danger-outline',
				},
				{
					'disabled:border-opacity-20 disabled:opacity-40':
						decoration === 'danger-outline',
				},

				// none
				{
					'text-black font-medium border-opacity-primary': !decoration,
				},

				{
					'border-none font-normal w-auto': decoration === 'ghost',
				},

				// global
				'group flex items-center justify-center border rounded-[100px] px-6 py-[14px]',
				'outline-none transition-colors duration-200',
				'focus:outline-none disabled:cursor-not-allowed font-semibold',
				'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
				className
			)}
		>
			{label ? (
				<div className="flex items-center justify-end">
					{icon && iconLeft && (
						<span className={clsx('w-6 h-6 mr-2', classNameButton)}>
							<Icon src={icon} />
						</span>
					)}
					<p className={clsx(classNameLabel, 'font-semibold text-base')}>
						<span>{label}</span>
					</p>{' '}
					{loading && <Spinner className="mx-2" />}
					{icon && iconRight && (
						<span className={clsx('w-6 h-6 ml-2', classNameButton)}>
							<Icon src={icon} />
						</span>
					)}
				</div>
			) : (
				children
			)}
		</button>
	);
});

export const Button = React.forwardRef<
	HTMLButtonElement,
	ButtonProps & CypressI & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function Button(
	{
		size = 'full',
		label,
		disabled,
		onClick,
		href,
		icon,
		children,
		iconLeft,
		iconRight,
		loading,
		isTargetBlack,
		classNameButton,
		classNameLink,
		classNameLabel,
		...props
	},
	ref
) {
	return (
		<>
			{href ? (
				<Link
					href={href}
					target={isTargetBlack ? '_blank' : undefined}
					className={clsx('w-full md:w-auto', classNameLink)}
				>
					<ButtonContent
						ref={ref}
						size={size}
						label={label}
						disabled={disabled}
						icon={icon}
						iconLeft={iconLeft}
						iconRight={iconRight}
						loading={loading}
						classNameLabel={classNameLabel}
						classNameButton={classNameButton}
						{...props}
					>
						{children}
					</ButtonContent>
				</Link>
			) : (
				<ButtonContent
					ref={ref}
					size={size}
					label={label}
					disabled={disabled}
					icon={icon}
					iconLeft={iconLeft}
					iconRight={iconRight}
					loading={loading}
					onClick={onClick}
					classNameLabel={classNameLabel}
					classNameButton={classNameButton}
					{...props}
				>
					{children}
				</ButtonContent>
			)}
		</>
	);
});
